<script setup lang="ts">
    import ValidateSiretEndpoint, { ValidatedSiretResponse } from '@/app/common/endpoints/ValidateSiretEndpoint';
    import { FormInputData, FormInputProps } from '@/composables/useFormInput';

    const emit = defineEmits(['validSiret', 'update:valid']);
    const props = defineProps<FormInputProps>();
    const inputData: FormInputData = useFormInput(props);
    const endpoint = new ValidateSiretEndpoint();
    const response = ref<ValidatedSiretResponse | null>(null);
    const validatingSiret = ref(false);
    const isDirty = ref(false);
    const { abortable, abort } = useAbortableFetch();

    const validateSiret = async () => {
        setIsDirty(false);
        abort();
        validatingSiret.value = true;
        response.value = null;
        if (inputData.form.errors?.has(inputData.inputName)) {
            inputData.form.errors?.clear(inputData.inputName);
        }

        // Strip spaces before validation
        if (inputData.model.value) {
            inputData.model.value = inputData.model.value.replace(/\s/g, '');
        }

        if (!inputData.model.value) {
            validatingSiret.value = false;
            return;
        }

        try {
            response.value = await endpoint.withAbortController(abortable.value).validate(inputData.model.value);
            if (response.value && response.value.data) {
                emit('validSiret', response.value);
            }

            validatingSiret.value = false;
        } catch (error) {}
    };

    const isValid = computed(() => {
        if (!inputData.model.value) return true;
        return !!response.value?.data;
    });

    watch(isValid, (value) => emit('update:valid', value));

    const setIsDirty = (value: boolean) => {
        isDirty.value = value;
    };
</script>

<template>
    <FormInput
        v-bind="props"
        @change="validateSiret"
        @input="() => setIsDirty(true)"
    >
        <template
            v-if="inputData.model.value"
            #after-input
        >
            <template v-if="validatingSiret">
                <span class="text-[11px] text-gray-400">
                    <i class="fa-duotone fa-loader fa-spin"></i>
                    {{ $t('misc.siret_input.validating') }}
                </span>
            </template>
            <template v-if="!validatingSiret && response && !isDirty">
                <span
                    v-if="response.error"
                    class="text-[11px] text-red-600"
                >
                    <i class="fa-regular fa-xmark" />
                    {{ response.error.message }}
                </span>
                <span
                    v-if="response.data"
                    class="text-[11px]"
                    :class="response.data.isOpen ? 'text-green-600' : 'text-orange-600'"
                >
                    <i class="fa-regular fa-check" />
                    {{ response.data.isOpen ? $t('misc.siret_input.valid') : $t('misc.siret_input.valid_but_closed') }}
                </span>
            </template>
        </template>
    </FormInput>
</template>
